<template>
  <section class="model-builder">
    <div class="model-builder__shell">
      <div class="row model-builder__layout">
        <div class="col-sm-8 pr-0 model-builder__main-column">
          <div class="model-builder__panel model-builder__panel--main">
            <header class="model-builder__header">
              <div class="row align-items-center">
                <div class="col-auto pr-0">
                  <button
                    type="button"
                    class="btn back_btn"
                    aria-label="Go back"
                    :disabled="submitted"
                    @click="handleBack"
                  >
                    <i class="fa fa-chevron-left" aria-hidden="true"></i>
                  </button>
                </div>
                <div class="col pl-0">
                  <h2 class="model-builder__title">Create Model</h2>
                </div>
              </div>
            </header>

            <div class="model-builder__content">
              <div class="model-builder__content-inner">
                <ol class="model-builder__steps" aria-label="Model creation progress">
                  <li
                    v-for="(stepLabel, index) in stepLabels"
                    :key="stepLabel"
                    class="model-builder__step"
                    :class="{
                      'model-builder__step--active': wizardStepIndex === index,
                      'model-builder__step--complete': wizardStepIndex > index
                    }"
                    :aria-current="wizardStepIndex === index ? 'step' : null"
                  >
                    <span class="model-builder__step-number">
                      <i v-if="wizardStepIndex > index" class="fa fa-check" aria-hidden="true"></i>
                      <template v-else>{{ index + 1 }}</template>
                    </span>
                    <span class="model-builder__step-label">{{ stepLabel }}</span>
                  </li>
                </ol>

                <form
                  v-if="currentStep === steps.DETAILS"
                  class="model-builder__card"
                  data-vv-scope="details"
                  novalidate
                  @submit.prevent="submitDetails"
                >
                  <div class="form-group model-builder__field">
                    <label for="model-name">Model Name</label>
                    <input
                      id="model-name"
                      v-model.trim="form.modelName"
                      v-validate="'required|max:100'"
                      type="text"
                      name="model_name"
                      class="form-control model-builder__control"
                      data-vv-as="model name"
                      autocomplete="off"
                      maxlength="100"
                      :class="{ 'is-invalid': errors.has('model_name', 'details') }"
                      :aria-invalid="errors.has('model_name', 'details')"
                      aria-describedby="model-name-error"
                    >
                    <small
                      v-if="errors.has('model_name', 'details')"
                      id="model-name-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('model_name', 'details') }}
                    </small>
                  </div>

                  <div class="form-group model-builder__field">
                    <label for="model-type">Data Type</label>
                    <div class="model-builder__dropdown">
                      <button
                        id="model-type"
                        type="button"
                        class="model-builder__control model-builder__custom-select"
                        :class="{
                          'is-invalid': errors.has('type_model', 'details'),
                          'is-open': isDropdownOpen('modelType')
                        }"
                        :aria-expanded="isDropdownOpen('modelType')"
                        aria-haspopup="listbox"
                        :aria-invalid="errors.has('type_model', 'details')"
                        aria-describedby="model-type-error"
                        @click="toggleDropdown('modelType')"
                        @keydown.esc="closeDropdown"
                      >
                        <span :class="{ placeholder: !form.modelType }">
                          {{ form.modelType ? modelTypeLabel(form.modelType) : 'Please Select' }}
                        </span>
                        <i class="fa fa-chevron-down model-builder__dropdown-arrow" aria-hidden="true"></i>
                      </button>

                      <div
                        v-if="isDropdownOpen('modelType')"
                        class="model-builder__dropdown-menu"
                        role="listbox"
                        aria-labelledby="model-type"
                      >
                        <button
                          v-for="modelType in modelTypes"
                          :key="modelType"
                          type="button"
                          class="model-builder__dropdown-option"
                          :class="{ 'is-selected': form.modelType === modelType }"
                          role="option"
                          :aria-selected="form.modelType === modelType"
                          @click="selectModelType(modelType)"
                        >
                          {{ modelTypeLabel(modelType) }}
                        </button>
                      </div>
                    </div>

                    <input
                      v-model="form.modelType"
                      v-validate="'required'"
                      type="hidden"
                      name="type_model"
                      data-vv-as="model type"
                    >
                    <small
                      v-if="errors.has('type_model', 'details')"
                      id="model-type-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('type_model', 'details') }}
                    </small>
                  </div>

                  <div class="form-group model-builder__field">
                    <label for="disease">Disease</label>
                    <div class="model-builder__dropdown">
                      <button
                        id="disease"
                        type="button"
                        class="model-builder__control model-builder__custom-select"
                        :class="{
                          'is-invalid': errors.has('category', 'details'),
                          'is-open': isDropdownOpen('disease')
                        }"
                        :aria-expanded="isDropdownOpen('disease')"
                        aria-haspopup="listbox"
                        :aria-invalid="errors.has('category', 'details')"
                        aria-describedby="disease-error"
                        @click="toggleDropdown('disease')"
                        @keydown.esc="closeDropdown"
                      >
                        <span :class="{ placeholder: !form.disease }">
                          {{ form.disease || 'Select Disease' }}
                        </span>
                        <i class="fa fa-chevron-down model-builder__dropdown-arrow" aria-hidden="true"></i>
                      </button>

                      <div
                        v-if="isDropdownOpen('disease')"
                        class="model-builder__dropdown-menu"
                        role="listbox"
                        aria-labelledby="disease"
                      >
                        <button
                          v-for="(disease, index) in diseaseOptions"
                          :key="disease.id || disease.name || index"
                          type="button"
                          class="model-builder__dropdown-option"
                          :class="{ 'is-selected': form.disease === disease.name }"
                          role="option"
                          :aria-selected="form.disease === disease.name"
                          @click="selectDisease(disease.name)"
                        >
                          {{ disease.name }}
                        </button>

                        <button
                          type="button"
                          class="model-builder__dropdown-option"
                          :class="{ 'is-selected': form.disease === 'Other' }"
                          role="option"
                          :aria-selected="form.disease === 'Other'"
                          @click="selectDisease('Other')"
                        >
                          Other
                        </button>
                      </div>
                    </div>

                    <input
                      v-model="form.disease"
                      v-validate="'required'"
                      type="hidden"
                      name="category"
                      data-vv-as="disease"
                    >
                    <small
                      v-if="errors.has('category', 'details')"
                      id="disease-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('category', 'details') }}
                    </small>
                  </div>

                  <div v-if="showSubDiseaseSelect" class="form-group model-builder__field">
                    <label for="sub-disease">Sub Disease</label>
                    <div class="model-builder__dropdown">
                      <button
                        id="sub-disease"
                        type="button"
                        class="model-builder__control model-builder__custom-select"
                        :class="{
                          'is-invalid': errors.has('sub_disease', 'details'),
                          'is-open': isDropdownOpen('subDisease')
                        }"
                        :aria-expanded="isDropdownOpen('subDisease')"
                        aria-haspopup="listbox"
                        :aria-invalid="errors.has('sub_disease', 'details')"
                        aria-describedby="sub-disease-error"
                        @click="toggleDropdown('subDisease')"
                        @keydown.esc="closeDropdown"
                      >
                        <span :class="{ placeholder: !form.subDisease }">
                          {{ form.subDisease || 'Select Sub Disease' }}
                        </span>
                        <i class="fa fa-chevron-down model-builder__dropdown-arrow" aria-hidden="true"></i>
                      </button>

                      <div
                        v-if="isDropdownOpen('subDisease')"
                        class="model-builder__dropdown-menu"
                        role="listbox"
                        aria-labelledby="sub-disease"
                      >
                        <button
                          v-for="(subDisease, index) in subDiseaseOptions"
                          :key="subDisease.id || subDisease.name || index"
                          type="button"
                          class="model-builder__dropdown-option"
                          :class="{ 'is-selected': form.subDisease === subDisease.name }"
                          role="option"
                          :aria-selected="form.subDisease === subDisease.name"
                          @click="selectSubDisease(subDisease.name)"
                        >
                          {{ subDisease.name }}
                        </button>
                      </div>
                    </div>

                    <input
                      v-model="form.subDisease"
                      v-validate="'required'"
                      type="hidden"
                      name="sub_disease"
                      data-vv-as="sub disease"
                    >
                    <small
                      v-if="errors.has('sub_disease', 'details')"
                      id="sub-disease-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('sub_disease', 'details') }}
                    </small>
                  </div>

                  <div v-if="showSubDiseaseText" class="form-group model-builder__field">
                    <label for="sub-disease-text">Sub Disease</label>
                    <textarea
                      id="sub-disease-text"
                      v-model.trim="form.subDisease"
                      v-validate="'required|max:50'"
                      name="sub_disease_text"
                      class="form-control model-builder__control model-builder__control--textarea"
                      data-vv-as="sub disease"
                      maxlength="50"
                      :class="{ 'is-invalid': errors.has('sub_disease_text', 'details') }"
                      :aria-invalid="errors.has('sub_disease_text', 'details')"
                      aria-describedby="sub-disease-text-error"
                    ></textarea>
                    <small
                      v-if="errors.has('sub_disease_text', 'details')"
                      id="sub-disease-text-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('sub_disease_text', 'details') }}
                    </small>
                  </div>

                  <div class="form-group model-builder__field">
                    <label for="algorithm">Algorithm</label>
                    <div class="model-builder__dropdown">
                      <button
                        id="algorithm"
                        type="button"
                        class="model-builder__control model-builder__custom-select"
                        :class="{
                          'is-invalid': errors.has('algorithm', 'details'),
                          'is-open': isDropdownOpen('algorithm'),
                          'is-disabled': form.autoSelect || !form.modelType
                        }"
                        :aria-expanded="isDropdownOpen('algorithm')"
                        aria-haspopup="listbox"
                        :aria-invalid="errors.has('algorithm', 'details')"
                        aria-describedby="algorithm-error"
                        :disabled="form.autoSelect || !form.modelType"
                        @click="toggleDropdown('algorithm')"
                        @keydown.esc="closeDropdown"
                      >
                        <span :class="{ placeholder: !form.algorithm }">
                          {{ form.autoSelect ? 'Algorithm will be selected automatically' : (form.algorithm || 'Select Algorithm') }}
                        </span>
                        <i class="fa fa-chevron-down model-builder__dropdown-arrow" aria-hidden="true"></i>
                      </button>

                      <div
                        v-if="isDropdownOpen('algorithm')"
                        class="model-builder__dropdown-menu"
                        role="listbox"
                        aria-labelledby="algorithm"
                      >
                        <button
                          v-for="algorithm in availableAlgorithms"
                          :key="algorithm"
                          type="button"
                          class="model-builder__dropdown-option"
                          :class="{ 'is-selected': form.algorithm === algorithm }"
                          role="option"
                          :aria-selected="form.algorithm === algorithm"
                          @click="selectAlgorithm(algorithm)"
                        >
                          {{ algorithm }}
                        </button>
                      </div>
                    </div>

                    <input
                      v-model="form.algorithm"
                      v-validate="{ required: !form.autoSelect }"
                      type="hidden"
                      name="algorithm"
                      data-vv-as="algorithm"
                    >
                    <small
                      v-if="errors.has('algorithm', 'details')"
                      id="algorithm-error"
                      class="invalid-feedback model-builder__error"
                    >
                      {{ errors.first('algorithm', 'details') }}
                    </small>
                  </div>

                  <div class="form-group model-builder__field">
                    <label for="auto-select" class="model-builder__checkbox mb-0">
                      <span class="model-builder__checkbox-box">
                        <input
                          id="auto-select"
                          v-model="form.autoSelect"
                          type="checkbox"
                          @change="handleAutoSelectChange"
                        >
                        <span><i class="fa fa-check" aria-hidden="true"></i></span>
                      </span>
                      <span>Auto Select</span>
                    </label>
                  </div>

                  <button class="btn model-builder__button model-builder__button--primary" type="submit" :disabled="submitted">
                    Next
                  </button>
                </form>

                <form
                  v-else-if="currentStep === steps.DATA"
                  class="model-builder__card"
                  data-vv-scope="data"
                  novalidate
                  @submit.prevent="trainModel"
                >
                  <div class="form-group model-builder__field">
                    <label for="existing-data-file" class="mb-3">Upload File</label>

                    <div v-if="!uploadedFile" class="model-builder__file-select">
                      <div class="model-builder__dropdown">
                        <button
                          id="existing-data-file"
                          type="button"
                          class="model-builder__control model-builder__custom-select"
                          :class="{
                            'is-open': isDropdownOpen('existingData'),
                            'is-disabled': Boolean(uploadedFile)
                          }"
                          :aria-expanded="isDropdownOpen('existingData')"
                          aria-haspopup="listbox"
                          :disabled="Boolean(uploadedFile)"
                          @click="toggleDropdown('existingData')"
                          @keydown.esc="closeDropdown"
                        >
                          <span :class="{ placeholder: !selectedDataId }">
                            {{ selectedDataFile ? selectedDataFile.data_file_name : 'Select Data File' }}
                          </span>
                          <i class="fa fa-chevron-down model-builder__dropdown-arrow" aria-hidden="true"></i>
                        </button>

                        <div
                          v-if="isDropdownOpen('existingData')"
                          class="model-builder__dropdown-menu"
                          role="listbox"
                          aria-labelledby="existing-data-file"
                        >
                          <button
                            v-for="dataFile in existingDataFiles"
                            :key="dataFile.id"
                            type="button"
                            class="model-builder__dropdown-option"
                            :class="{ 'is-selected': selectedDataId === String(dataFile.id) }"
                            role="option"
                            :aria-selected="selectedDataId === String(dataFile.id)"
                            @click="selectExistingDataFile(String(dataFile.id))"
                          >
                            {{ dataFile.data_file_name }}
                          </button>
                        </div>
                      </div>
                      <button
                        v-if="selectedDataFile"
                        type="button"
                        class="model-builder__clear-button"
                        title="Clear selected file"
                        aria-label="Clear selected file"
                        :disabled="submitted"
                        @click="clearExistingFile"
                      >
                        <i class="fa fa-times" aria-hidden="true"></i>
                      </button>
                    </div>

                    <div v-if="existingDataFiles.length && !hasDataSource" class="model-builder__separator">
                      <span>OR</span>
                    </div>

                    <div v-if="!selectedDataFile" class="model-builder__upload">
                      <div class="model-builder__upload-control">
                        <label for="upload-attachment" :title="uploadFileLabel">
                          <input
                            id="upload-attachment"
                            ref="fileInput"
                            v-validate="{ required: !selectedDataFile }"
                            type="file"
                            name="file"
                            data-vv-as="data file"
                            :accept="acceptedFileTypes"
                            :disabled="submitted"
                            @change="handleFileUpload"
                          >
                          <span>
                            <i v-if="!uploadedFile" class="fa fa-upload mr-2" aria-hidden="true"></i>
                            {{ uploadFileLabel }}
                          </span>
                        </label>
                        <button
                          v-if="uploadedFile"
                          type="button"
                          class="model-builder__clear-button"
                          title="Clear uploaded file"
                          aria-label="Clear uploaded file"
                          :disabled="submitted"
                          @click="clearUploadedFile"
                        >
                          <i class="fa fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                      <small class="model-builder__hint">
                        Excel files only, up to {{ maxUploadSizeMb }} MB.
                      </small>
                      <small
                        v-if="errors.has('file', 'data')"
                        class="invalid-feedback model-builder__error d-block"
                      >
                        {{ errors.first('file', 'data') }}
                      </small>
                      <small v-else-if="fileValidationError" class="invalid-feedback model-builder__error d-block">
                        {{ fileValidationError }}
                      </small>
                    </div>
                  </div>

                  <button class="btn model-builder__button model-builder__button--primary" type="submit" :disabled="submitted || !hasDataSource">
                    {{ submitted ? 'Training…' : 'Train' }}
                  </button>

                  <div v-if="showTrainingProgress" class="model-builder__progress" aria-live="polite">
                    <percentage-loader
                      v-if="uploadProgress > 0"
                      :upload-progress-state="uploadProgress"
                    />
                    <p v-else class="model-builder__status mb-0">Preparing and training your model…</p>
                  </div>
                </form>

                <div
                  v-else-if="currentStep === steps.RECOMMENDATION"
                  class="model-builder__card model-builder__recommendation"
                >
                  <h3 class="model-builder__section-title">Algorithm Selected Successfully</h3>

                  <form @submit.prevent="saveRecommendedModel">
                    <div class="form-group model-builder__field">
                      <label for="recommended-model-name">Model Name</label>
                      <input
                        id="recommended-model-name"
                        type="text"
                        class="form-control model-builder__control"
                        :value="form.modelName"
                        readonly
                      >
                    </div>

                    <section v-if="recommendedParameter" aria-labelledby="saved-algorithm-heading">
                      <h4 id="saved-algorithm-heading" class="model-builder__section-subtitle mb-0">
                        Saved Algorithm
                      </h4>
                      <p class="model-builder__description">
                        Our AI system selected the algorithm with the strongest performance for this data.
                      </p>
                      <article class="model-builder__algorithm-card model-builder__algorithm-card--recommended">
                        <div class="model-builder__algorithm-card-content">
                          <span class="model-builder__algorithm-name">{{ algorithmNameFor(recommendedParameter) }}</span>
                          <template v-if="recommendedModelType === 'Classification'">
                            <span class="model-builder__algorithm-metric">F1 Score: {{ displayValue(recommendedParameter.f1_score) }}</span>
                            <span class="model-builder__algorithm-metric">AUC-ROC: {{ displayValue(recommendedParameter.auc_roc) }}</span>
                            <span class="model-builder__algorithm-metric">Accuracy: {{ displayValue(recommendedParameter.accuracy) }}%</span>
                          </template>
                          <span v-else class="model-builder__algorithm-metric">
                            R Square: {{ displayValue(recommendedParameter.r_square) }}
                          </span>
                          <span class="badge model-builder__badge">Recommended</span>
                        </div>
                      </article>
                    </section>

                    <section
                      v-if="comparativeParameters.length"
                      class="model-builder__comparison"
                      aria-labelledby="comparative-algorithms-heading"
                    >
                      <h4 id="comparative-algorithms-heading" class="model-builder__section-subtitle mb-0">
                        Comparative Algorithms
                      </h4>
                      <p class="model-builder__description">
                        Compare the other algorithms with the recommended result above.
                      </p>
                      <article
                        v-for="parameter in comparativeParameters"
                        :key="parameter._key"
                        class="model-builder__algorithm-card"
                      >
                        <div class="model-builder__algorithm-card-content">
                          <span class="model-builder__algorithm-name">{{ algorithmNameFor(parameter) }}</span>
                          <template v-if="recommendedModelType === 'Classification'">
                            <span class="model-builder__algorithm-metric">F1 Score: {{ displayValue(parameter.f1_score) }}</span>
                            <span class="model-builder__algorithm-metric">AUC-ROC: {{ displayValue(parameter.auc_roc) }}</span>
                            <span class="model-builder__algorithm-metric">Accuracy: {{ displayValue(parameter.accuracy) }}%</span>
                          </template>
                          <span v-else class="model-builder__algorithm-metric">
                            R Square: {{ displayValue(parameter.r_square) }}
                          </span>
                        </div>
                      </article>
                    </section>

                    <button class="btn model-builder__button model-builder__button--primary" type="submit" :disabled="submitted">
                      {{ submitted ? 'Saving…' : 'Proceed' }}
                    </button>
                  </form>
                </div>

                <div
                  v-else-if="currentStep === steps.RESULT"
                  class="model-builder__card model-builder__result"
                >
                  <div class="model-builder__success-icon" aria-hidden="true">
                    <i class="fa fa-check"></i>
                  </div>
                  <h3 class="model-builder__section-title">
                    Your model was successfully trained using
                    <span class="model-builder__accent">{{ trainedAlgorithmName }}</span>.
                  </h3>

                  <div class="form-group model-builder__field">
                    <label for="trained-model-name">Model Name</label>
                    <input
                      id="trained-model-name"
                      type="text"
                      class="form-control model-builder__control"
                      :value="form.modelName"
                      readonly
                    >
                  </div>

                  <h3 class="model-builder__metrics-title">Performance Metrics</h3>

                  <ul v-if="performanceMetrics.length" class="model-builder__metric-options">
                    <li
                      v-for="metric in performanceMetrics"
                      :key="metric.name"
                      class="model-builder__metric-wrapper"
                    >
                      <div class="model-builder__metric-option">
                        <label :for="metricInputId(metric.name)" class="model-builder__accent">
                          <input
                            :id="metricInputId(metric.name)"
                            type="checkbox"
                            :checked="isMetricEnabled(metric.name)"
                            :disabled="Boolean(updatingMetrics[metric.name])"
                            @change="updatePerformanceMetric(metric.name, $event.target.checked)"
                          >
                          {{ metricDisplayName(metric.name) }}
                        </label>

                        <button
                          v-if="metric.desc"
                          type="button"
                          class="model-builder__tooltip-btn"
                          :class="{ 'is-active': activeTooltip === metric.name }"
                          :aria-expanded="activeTooltip === metric.name"
                          :aria-label="`Toggle information for ${metricDisplayName(metric.name)}`"
                          @click.stop="toggleTooltip(metric.name)"
                        >
                          <i class="fa fa-info-circle" aria-hidden="true"></i>
                        </button>
                      </div>

                      <transition name="slide-fade">
                        <div
                          v-if="activeTooltip === metric.name"
                          class="model-builder__tooltip-dropdown"
                          role="region"
                        >
                          <div class="model-builder__tooltip-arrow"></div>
                          <p class="mb-0">{{ metric.desc }}</p>
                        </div>
                      </transition>
                    </li>
                  </ul>

                  <div v-if="trainedModelInput" class="model-builder__metric-values">
                    <template v-if="isRegressionModel">
                      <p>
                        Root Mean Squared Error:
                        <span class="model-builder__accent">{{ displayValue(trainedModelInput.rmse) }}</span>
                      </p>
                      <p class="model-builder__description">
                        Regression models are evaluated with error metrics such as RMSE rather than classification accuracy.
                      </p>
                    </template>
                    <p v-else>
                      Accuracy:
                      <span class="model-builder__accent">{{ displayValue(trainedModelInput.acc) }}%</span>
                    </p>

                    <p
                      v-for="metric in visibleMetricValues"
                      :key="metric.name"
                    >
                      {{ metricDisplayName(metric.name) }}:
                      <span class="model-builder__accent">{{ metric.value }}</span>
                    </p>
                  </div>

                  <router-link
                    v-if="createdModelId"
                    :to="{ name: 'aiModelPrediction', params: { modelId: createdModelId } }"
                    class="btn model-builder__button model-builder__button--primary"
                  >
                    Use Model
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside class="col-sm-4 model-builder__sidebar-column">
          <div class="model-builder__panel model-builder__panel--sidebar">
            <div class="model-builder__recent">
              <h3 class="model-builder__sidebar-title">Most Used Models</h3>

              <ul v-if="recentModels.length" class="model-builder__recent-list">
                <li
                  v-for="(model, index) in recentModels"
                  :key="model.id || model.name || index"
                  class="model-builder__recent-item"
                >
                  <div class="row align-items-center">
                    <div class="col-5 pr-0">
                      <span class="model-builder__recent-name" :title="model.name">
                        {{ model.name }}
                      </span>
                      <span :title="model.display_name_algorithm">
                        {{ model.display_name_algorithm }}
                      </span>
                    </div>
                    <div class="col pr-0 pl-1 text-center">
                      <span>{{ model.date | LocalDateFormat }}</span>
                    </div>
                    <div class="col-auto ml-auto text-right">
                      <router-link
                        :to="{ name: 'aiModelPrediction', params: { modelId: model.id } }"
                        class="btn model-builder__button model-builder__button--secondary"
                      >
                        Use
                      </router-link>
                    </div>
                  </div>
                </li>
              </ul>

              <p v-else class="model-builder__empty-state">
                You don't have any models yet.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import percentageLoader from '@/components/common/percentage-loader-f-file';
import root from '@/_helpers/roots';
import toast from '@/services/toast';

const STEPS = Object.freeze({
  DETAILS: 'details',
  DATA: 'data',
  RECOMMENDATION: 'recommendation',
  RESULT: 'result'
});

const MODEL_TYPES = Object.freeze(['Classification', 'Regression']);
const STEP_LABELS = Object.freeze(['Model setup', 'Training data', 'Results']);
const CLASSIFICATION_ALGORITHMS = Object.freeze([
  'Logistic Regression',
  'Decision Tree Classifier',
  'Random Forest Classifier',
  'Gradient Boosting Classifier',
  'Support Vector Classifier',
  'Gaussian Naive Bias',
  'K-Neighbors Classifier'
]);
const REGRESSION_ALGORITHMS = Object.freeze([
  'Linear Regression',
  'Decision Tree Regressor',
  'Random Forest Regressor',
  'Gradient Boosting Regressor',
  'Support Vector Regressor',
  'K-Neighbors Regressor'
]);

const ALLOWED_FILE_EXTENSIONS = Object.freeze([
  'xls', 'xlt', 'xlm', 'xlsx', 'xltx', 'xltm', 'xlsb',
  'xla', 'xlam', 'xll', 'xlw', 'xlsm'
]);
const MAX_UPLOAD_SIZE_MB = 100;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
const MODELS_API_URL = root.modelsForAITaskApiUrl.link;

const METRIC_VALUE_KEYS = Object.freeze({
  MCC: 'mcc',
  Sensitivity: 'sensitivity',
  Specificity: 'specificity',
  Kappa: 'kappa',
  F1_Score: 'f1',
  R_Square: 'r_square',
  MAE: 'mae'
});

export default {
  name: 'CreateNewModels',
  components: {
    percentageLoader
  },
  data() {
    return {
      steps: STEPS,
      stepLabels: STEP_LABELS,
      currentStep: STEPS.DETAILS,
      openDropdown: null,
      activeTooltip: null,
      modelTypes: MODEL_TYPES,
      submitted: false,
      showTrainingProgress: false,
      uploadProgress: 0,
      form: {
        modelName: '',
        modelType: '',
        disease: '',
        subDisease: '',
        diseaseParentId: '',
        algorithm: '',
        autoSelect: false
      },
      subDiseaseOptions: [],
      showSubDiseaseSelect: false,
      showSubDiseaseText: false,
      existingDataFiles: [],
      selectedDataId: '',
      selectedDataFile: null,
      uploadedFile: null,
      fileValidationError: '',
      isPublic: false,
      createdModelDetails: null,
      recommendedModelData: null,
      performanceParametersData: null,
      metricVisibility: {},
      updatingMetrics: {},
      requestSource: null
    };
  },
  computed: {
    ...mapGetters({
      mostUsedModelsStore: 'getMostUsedTrainModelForAiDashGetters',
      diseasesStore: 'getDiseaseForAiDashGetters'
    }),
    recentModels() {
      return this.getResults(this.mostUsedModelsStore);
    },
    wizardStepIndex() {
      if (this.currentStep === STEPS.DETAILS) {
        return 0;
      }
      if (this.currentStep === STEPS.DATA) {
        return 1;
      }
      return 2;
    },
    diseaseOptions() {
      return this.getResults(this.diseasesStore);
    },
    availableAlgorithms() {
      if (this.form.modelType === 'Classification') {
        return CLASSIFICATION_ALGORITHMS;
      }
      if (this.form.modelType === 'Regression') {
        return REGRESSION_ALGORITHMS;
      }
      return [];
    },
    acceptedFileTypes() {
      return ALLOWED_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');
    },
    maxUploadSizeMb() {
      return MAX_UPLOAD_SIZE_MB;
    },
    hasDataSource() {
      return Boolean(this.selectedDataFile || this.uploadedFile) && !this.fileValidationError;
    },
    uploadFileLabel() {
      return this.uploadedFile ? this.uploadedFile.name : 'Choose attachment';
    },
    normalizedRecommendedParameters() {
      const parameters = this.recommendedModelData && this.recommendedModelData.parameters_list;
      if (!Array.isArray(parameters)) {
        return [];
      }

      return parameters.reduce((normalized, entry, index) => {
        const value = entry && Object.prototype.hasOwnProperty.call(entry, index)
          ? entry[index]
          : entry;
        if (!value || typeof value !== 'object') {
          return normalized;
        }

        normalized.push(Object.assign({}, value, {
          _key: value.id || `${value.algoIndex}-${index}`
        }));
        return normalized;
      }, []);
    },
    recommendedParameter() {
      return this.normalizedRecommendedParameters.find((parameter) => parameter.recommended) || null;
    },
    comparativeParameters() {
      return this.normalizedRecommendedParameters.filter((parameter) => !parameter.recommended);
    },
    recommendedModelType() {
      return (this.recommendedModelData && this.recommendedModelData.type) || this.form.modelType;
    },
    trainedModelInput() {
      return this.createdModelDetails && this.createdModelDetails.input_data
        ? this.createdModelDetails.input_data
        : null;
    },
    createdModelId() {
      return this.trainedModelInput && this.trainedModelInput.id;
    },
    trainedAlgorithmName() {
      return (this.trainedModelInput && this.trainedModelInput.algorithm) || this.form.algorithm || 'the selected algorithm';
    },
    isRegressionModel() {
      return Boolean(this.trainedModelInput && this.trainedModelInput.type_model === 'Regression');
    },
    performanceMetrics() {
      if (!this.performanceParametersData) {
        return [];
      }
      const type = this.isRegressionModel ? 'Regression' : 'Classification';
      return Array.isArray(this.performanceParametersData[type])
        ? this.performanceParametersData[type]
        : [];
    },
    visibleMetricValues() {
      if (!this.trainedModelInput) {
        return [];
      }

      return this.performanceMetrics.reduce((metrics, metric) => {
        const valueKey = METRIC_VALUE_KEYS[metric.name];
        const value = valueKey ? this.trainedModelInput[valueKey] : null;
        if (this.isMetricEnabled(metric.name) && this.hasDisplayValue(value)) {
          metrics.push({
            name: metric.name,
            value: this.formatMetricValue(metric.name, value)
          });
        }
        return metrics;
      }, []);
    }
  },
  mounted() {
    this.loadExistingDataFiles();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.requestSource) {
      this.requestSource.cancel('Create model component was destroyed.');
    }
  },
  methods: {
    getResults(storeValue) {
      const payload = storeValue && storeValue.data ? storeValue.data : storeValue;
      return payload && Array.isArray(payload.results) ? payload.results : [];
    },
    modelTypeLabel(modelType) {
      return modelType === 'Regression'
        ? 'Regression (continuous numerical value prediction)'
        : modelType;
    },
    handleBack() {
      if (this.currentStep === STEPS.DATA) {
        this.currentStep = STEPS.DETAILS;
        return;
      }
      this.$router.go(-1);
    },
    handleModelTypeChange() {
      this.form.algorithm = '';
      this.removeValidationError('algorithm', 'details');
    },
    handleAutoSelectChange() {
      if (this.form.autoSelect) {
        this.form.algorithm = '';
        this.removeValidationError('algorithm', 'details');
      }
    },
    isDropdownOpen(name) {
      return this.openDropdown === name;
    },
    toggleDropdown(name) {
      if (name === 'algorithm' && (this.form.autoSelect || !this.form.modelType)) {
        return;
      }
      if (name === 'existingData' && this.uploadedFile) {
        return;
      }
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    closeDropdown() {
      this.openDropdown = null;
    },
    toggleTooltip(metricName) {
      this.activeTooltip = this.activeTooltip === metricName ? null : metricName;
    },
    handleDocumentClick(event) {
      if (!event.target.closest('.model-builder__dropdown')) {
        this.closeDropdown();
      }
      if (!event.target.closest('.model-builder__metric-wrapper')) {
        this.activeTooltip = null;
      }
    },
    selectModelType(modelType) {
      this.form.modelType = modelType;
      this.closeDropdown();
      this.handleModelTypeChange();
    },
    selectDisease(diseaseName) {
      this.form.disease = diseaseName;
      this.closeDropdown();
      this.handleDiseaseChange();
    },
    selectSubDisease(subDiseaseName) {
      this.form.subDisease = subDiseaseName;
      this.closeDropdown();
      this.handleSubDiseaseChange();
    },
    selectAlgorithm(algorithm) {
      this.form.algorithm = algorithm;
      this.closeDropdown();
    },
    selectExistingDataFile(dataId) {
      this.selectedDataId = dataId;
      this.closeDropdown();
      this.handleExistingFileChange();
    },
    handleDiseaseChange() {
      this.form.subDisease = '';
      this.form.diseaseParentId = '';
      this.subDiseaseOptions = [];
      this.showSubDiseaseSelect = false;
      this.showSubDiseaseText = this.form.disease === 'Other';

      if (this.form.disease === 'Other') {
        this.clearSubDiseaseErrors();
        return;
      }

      const disease = this.diseaseOptions.find((item) => item.name === this.form.disease);
      if (!disease) {
        return;
      }

      this.showSubDiseaseText = Boolean(disease.has_text_sub_disease);
      if (Array.isArray(disease.sub_disease) && disease.sub_disease.length) {
        this.subDiseaseOptions = disease.sub_disease;
        this.showSubDiseaseSelect = !this.showSubDiseaseText;
      }
      this.clearSubDiseaseErrors();
    },
    handleSubDiseaseChange() {
      const selected = this.subDiseaseOptions.find((item) => item.name === this.form.subDisease);
      this.form.diseaseParentId = selected && selected.parent ? selected.parent : '';
    },
    clearSubDiseaseErrors() {
      this.removeValidationError('sub_disease', 'details');
      this.removeValidationError('sub_disease_text', 'details');
    },
    removeValidationError(field, scope) {
      if (this.errors && typeof this.errors.remove === 'function') {
        this.errors.remove(field, scope);
      }
    },
    async submitDetails() {
      const isValid = await this.$validator.validateAll('details');
      if (!isValid) {
        return;
      }
      this.currentStep = STEPS.DATA;
    },
    async loadExistingDataFiles() {
      try {
        const response = await this.$store.dispatch('getAllAlgoDataForModelTrainedAction');
        const data = response && response.data && response.data.data;
        this.existingDataFiles = data && Array.isArray(data.results) ? data.results : [];
      } catch (error) {
        this.existingDataFiles = [];
        toast.error(this.getErrorMessage(error, 'Unable to load existing data files.'));
      }
    },
    handleExistingFileChange() {
      this.selectedDataFile = this.existingDataFiles.find(
        (dataFile) => String(dataFile.id) === this.selectedDataId
      ) || null;

      if (this.selectedDataFile) {
        this.clearUploadedFile();
        this.removeValidationError('file', 'data');
      }
    },
    clearExistingFile() {
      this.selectedDataId = '';
      this.selectedDataFile = null;
    },
    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      this.fileValidationError = '';

      if (!file) {
        this.uploadedFile = null;
        return;
      }

      const validationError = this.validateUploadFile(file);
      if (validationError) {
        this.fileValidationError = validationError;
        this.uploadedFile = null;
        event.target.value = '';
        return;
      }

      this.clearExistingFile();
      this.uploadedFile = file;
      this.removeValidationError('file', 'data');
    },
    validateUploadFile(file) {
      const extension = file.name.indexOf('.') !== -1
        ? file.name.split('.').pop().toLowerCase()
        : '';

      if (ALLOWED_FILE_EXTENSIONS.indexOf(extension) === -1) {
        return 'Please select a supported Excel file.';
      }
      if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        return `The selected file must be ${MAX_UPLOAD_SIZE_MB} MB or smaller.`;
      }
      if (file.size === 0) {
        return 'The selected file is empty.';
      }
      return '';
    },
    clearUploadedFile() {
      this.uploadedFile = null;
      this.fileValidationError = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async trainModel() {
      const isValid = await this.$validator.validateAll('data');
      if (!isValid || !this.hasDataSource || this.submitted) {
        return;
      }

      this.submitted = true;
      this.showTrainingProgress = true;
      this.uploadProgress = 0;
      this.requestSource = axios.CancelToken.source();

      try {
        if (this.uploadedFile) {
          await this.persistUploadedData();
        }

        const response = await axios.post(
          MODELS_API_URL,
          this.buildTrainingFormData(),
          {
            cancelToken: this.requestSource.token,
            onUploadProgress: this.updateUploadProgress
          }
        );

        this.createdModelDetails = response.data.data;
        if (this.form.autoSelect) {
          await this.loadRecommendedModel();
          this.currentStep = STEPS.RECOMMENDATION;
        } else {
          await this.loadPerformanceParameters();
          this.currentStep = STEPS.RESULT;
        }

        toast.success(response.data.msg || 'Model trained successfully.');
      } catch (error) {
        if (!axios.isCancel(error)) {
          toast.error(this.getErrorMessage(error, 'Unable to train the model.'));
        }
      } finally {
        this.submitted = false;
        this.showTrainingProgress = false;
        this.requestSource = null;
      }
    },
    buildTrainingFormData() {
      const formData = new FormData();
      formData.append('name', this.form.modelName);
      formData.append('disease', this.form.disease);
      formData.append('algorithm', this.form.algorithm);
      formData.append('display_name_algorithm', this.form.algorithm);
      formData.append('parent', this.form.diseaseParentId);
      formData.append('subDisease', this.form.subDisease);
      formData.append('type_model', this.form.modelType);

      if (this.form.autoSelect) {
        formData.append('auto_select', true);
      }

      if (this.selectedDataFile) {
        formData.append('data_url', this.selectedDataFile.data_file);
        formData.append('data_url_name', this.selectedDataFile.data_file_name);
        formData.append('data', this.selectedDataFile.id);
      } else {
        formData.append('data_file', this.uploadedFile);
        formData.append('data_file_name', this.uploadedFile.name);
      }

      return formData;
    },
    async persistUploadedData() {
      const formData = new FormData();
      formData.append('category', this.form.disease);
      formData.append('data_file', this.uploadedFile);
      formData.append('data_file_name', this.uploadedFile.name);
      formData.append('is_public', this.isPublic);
      await this.$store.dispatch('createAlgoDataDetailsAction', formData);
    },
    updateUploadProgress(progressEvent) {
      if (!progressEvent.total) {
        return;
      }
      this.uploadProgress = Math.min(
        100,
        Math.round((progressEvent.loaded / progressEvent.total) * 100)
      );
    },
    async loadRecommendedModel() {
      const response = await this.$store.dispatch('getRecommendedParametersAction');
      this.recommendedModelData = response.data.data.input_data;
    },
    async loadPerformanceParameters() {
      const response = await this.$store.dispatch('getModelPerformanceParameterListAction');
      this.performanceParametersData = response.data.data;
      this.initializeMetricVisibility();
    },
    initializeMetricVisibility() {
      const allMetrics = [];
      const parameterData = this.performanceParametersData || {};
      Object.keys(parameterData).forEach((type) => {
        if (Array.isArray(parameterData[type])) {
          allMetrics.push(...parameterData[type]);
        }
      });

      this.metricVisibility = allMetrics.reduce((visibility, metric) => {
        visibility[metric.name] = true;
        return visibility;
      }, {});
    },
    algorithmNameFor(parameter) {
      const algorithms = this.recommendedModelType === 'Classification'
        ? CLASSIFICATION_ALGORITHMS
        : REGRESSION_ALGORITHMS;
      return algorithms[parameter.algoIndex] || 'Unknown algorithm';
    },
    async saveRecommendedModel() {
      if (this.submitted || !this.recommendedModelData) {
        return;
      }

      this.submitted = true;
      const payload = {
        id: this.recommendedModelData.id,
        type: this.recommendedModelData.type,
        parameters_list: []
      };

      try {
        const response = await this.$store.dispatch('saveRecommenededParametersAction', payload);
        this.createdModelDetails = response.data.data;
        await this.loadPerformanceParameters();
        this.currentStep = STEPS.RESULT;
      } catch (error) {
        toast.error(this.getErrorMessage(error, 'Unable to save the recommended model.'));
      } finally {
        this.submitted = false;
      }
    },
    metricInputId(metricName) {
      return `metric-${metricName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    },
    metricDisplayName(metricName) {
      return metricName === 'R_Square'
        ? 'R Square'
        : metricName === 'F1_Score'
          ? 'F1 Score'
          : metricName;
    },
    isMetricEnabled(metricName) {
      return this.metricVisibility[metricName] !== false;
    },
    async updatePerformanceMetric(metricName, enabled) {
      if (!this.createdModelId) {
        return;
      }

      const previousValue = this.isMetricEnabled(metricName);
      this.$set(this.metricVisibility, metricName, enabled);
      this.$set(this.updatingMetrics, metricName, true);

      try {
        await this.$store.dispatch('updateModelInfoByIdAction', {
          model_id: this.createdModelId,
          [metricName]: enabled ? 'True' : 'False'
        });
        toast.success('Performance parameter updated.');
      } catch (error) {
        this.$set(this.metricVisibility, metricName, previousValue);
        toast.error(this.getErrorMessage(error, 'Unable to update the performance parameter.'));
      } finally {
        this.$delete(this.updatingMetrics, metricName);
      }
    },
    hasDisplayValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    displayValue(value) {
      return this.hasDisplayValue(value) ? value : 'N/A';
    },
    formatMetricValue(metricName, value) {
      const suffix = metricName === 'Sensitivity' || metricName === 'Specificity' ? '%' : '';
      return `${this.displayValue(value)}${suffix}`;
    },
    getErrorMessage(error, fallbackMessage) {
      const responseData = error && error.response && error.response.data;
      const directData = error && error.data;
      const data = responseData || directData || {};
      return data.user_msg || data.msg || data.message || fallbackMessage;
    }
  }
};
</script>

<style lang="scss" scoped>
$color-surface: rgba(0, 0, 0, 0.76);
$color-border: rgba(255, 255, 255, 0.13);$color-text: #f7f9fc;
$color-muted: #b5b5b5;
$color-accent: #ffd400;
$color-info: #ffd43b;
$color-success: #55d98a;
$color-danger: #ff6b6b;
$shadow-panel: 0 18px 45px rgba(0, 0, 0, 0.24);$shadow-focus: 0 0 0 3px rgba(255, 212, 59, 0.2);

.model-builder {
  color: $color-text;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  line-height: 1.5;

  &__shell {
    position: relative;
    width: 100%;
    min-height: calc(100dvh - 80px);
    padding: 8px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 3px;
  }

  &__layout {
    margin-right: -8px;
    margin-left: -8px;
  }

  &__main-column,
  &__sidebar-column {
    padding-right: 8px;
    padding-left: 8px;
  }

  &__panel {
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 4px;

    &--sidebar {
      min-height: calc(100vh - 100px);
      min-height: calc(100dvh - 100px);
      padding: 10px 15px;
    }
  }

  &__header {
    padding: 10px 20px;
  }

  &__eyebrow {
    display: block;
    margin-bottom: 2px;
    color: $color-accent;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  &__title {
    margin: 0;
    color: #f7f9fc;
    font-size: 24px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__content {
    padding: 28px;
  }

  &__content-inner {
    max-width: 680px;
    margin: 0 auto;
  }

  &__steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 0 0 24px;
    padding: 0 12px;
    list-style: none;
  }

  &__step {
    position: relative;
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    color: #858585;
    font-size: 12px;
    font-weight: 600;
    text-align: center;

    &::after {
      position: absolute;
      top: 16px;
      left: calc(50% + 23px);
      width: calc(100% - 46px);
      height: 2px;
      background: rgba(255, 255, 255, 0.08);
      content: "";
    }

    &:last-child::after {
      display: none;
    }

    &--active {
      color: $color-text;
    }

    &--complete {
      color: $color-success;

      &::after {
        background: rgba(85, 217, 138, 0.55);
      }
    }
  }

  &__step-number {
    position: relative;
    z-index: 1;
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    color: inherit;
    background: $color-surface;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 50%;
  }

  &__step--active &__step-number {
    color: #101522;
    background: $color-accent;
    border-color: $color-accent;
  }

  &__step--complete &__step-number {
    color: #0c1a12;
    background: $color-success;
    border-color: $color-success;
  }

  &__step-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__card {
    padding: 30px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 14px;
  }

  &__field {
    margin-bottom: 20px;

    > label {
      margin-bottom: 7px;
      color: #e2e2e2;
      font-size: 14px;
      font-weight: 600;
    }
  }

  &__control {
    width: 100%;
    height: 50px;
    padding: 10px 15px;
    color: $color-accent;
    font-size: 15px;
    background-color: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    box-shadow: none !important;
    transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;

    &:hover:not(:disabled):not([readonly]) {
      border-color: rgba(255, 255, 255, 0.22);
    }

    &:focus,
    &:hover {
      color: $color-accent;
      background-color: rgba(0, 0, 0, 0.72);
      border-color: $color-accent !important;
    }

    &.is-invalid {
      border-color: $color-danger;
    }

    &:disabled,
    &[readonly] {
      color: #858585;
      background-color: rgba(0, 0, 0, 0.28);
      cursor: not-allowed;
      opacity: 1;
    }

    &--textarea {
      min-height: 96px;
      resize: vertical;
    }
  }

  &__dropdown {
    position: relative;
    width: 100%;
  }

  &__custom-select {
    position: relative;
    display: flex;
    width: 100%;
    height: 50px;
    align-items: center;
    justify-content: space-between;
    padding: 10px 42px 10px 15px;
    color: $color-accent;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
    background-color: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    box-shadow: none;
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;

    &:hover {
      border-color: rgba(255, 212, 59, 0.55);
    }

    &:focus {
      outline: none;
      color: $color-text;
      background-color: rgba(0, 0, 0, 0.72);
      border-color: $color-accent;
    }

    &.is-open {
      background-color: rgba(0, 0, 0, 0.72);
      border-color: $color-accent;
      border-radius: 8px 8px 0 0;
    }

    &.is-invalid {
      border-color: $color-danger;
    }

    &.is-disabled,
    &:disabled {
      color: #858585;
      background-color: rgba(0, 0, 0, 0.28);
      border-color: rgba(255, 255, 255, 0.08);
      cursor: not-allowed;
      opacity: 1;
    }

    .placeholder {
      color: #858585;
    }
  }

  &__dropdown-arrow {
    position: absolute;
    top: 50%;
    right: 15px;
    color: #858585;
    font-size: 11px;
    pointer-events: none;
    transform: translateY(-50%);
    transition: transform 160ms ease;
  }

  &__custom-select.is-open &__dropdown-arrow {
    transform: translateY(-50%) rotate(180deg);
  }

  &__dropdown-menu {
    position: absolute;
    top: calc(100% - 1px);
    right: 0;
    left: 0;
    z-index: 1050;
    max-height: 220px;
    padding: 5px 0;
    overflow-y: auto;
    background: linear-gradient(180deg, rgba(30, 30, 30, 0.97), rgba(18, 18, 18, 0.97));
    border: 1px solid $color-accent;
    border-top: 0;
    border-radius: 0 0 10px 10px;
    box-shadow: 0 18px 35px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);

    &::-webkit-scrollbar {
      width: 7px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.03);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 212, 59, 0.35);
      border-radius: 10px;
    }
  }

  &__dropdown-option {
    display: block;
    width: 100%;
    margin: 0;
    padding: 9px 15px;
    color: $color-accent;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0;
    cursor: pointer;
    transition: background-color 120ms ease, color 120ms ease, padding-left 120ms ease;

    &:hover,
    &:focus {
      padding-left: 19px;
      color: #ffffff;
      background: rgba(255, 212, 59, 0.12);
      outline: none;
    }

    &.is-selected {
      color: #ffffff;
      background: rgba(255, 212, 59, 0.16);
    }
  }

  &__error {
    margin-top: 6px;
    color: $color-danger;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
  }

  &__checkbox {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #e2e2e2;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  &__checkbox-box {
    position: relative;
    display: inline-flex;

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
    }

    span {
      display: inline-flex;
      width: 22px;
      height: 22px;
      align-items: center;
      justify-content: center;
      color: #111111;
      font-size: 12px;
      background: transparent;
      border: 2px solid rgba(255, 212, 59, 0.7);
      border-radius: 4px;
      transition: background-color 150ms ease, box-shadow 150ms ease;
    }

    input:not(:checked) + span .fa {
      visibility: hidden;
    }

    input:checked + span {
      background: $color-accent;
      border-color: $color-accent;
    }

    input:focus-visible + span {
      box-shadow: $shadow-focus;
    }
  }

  &__button {
    display: inline-flex;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    padding: 8px 25px;
    font-size: 14px;
    font-weight: 700;
    border: 1px solid transparent;
    border-radius: 8px;
    box-shadow: none;
    transition: transform 150ms ease, background-color 150ms ease, box-shadow 150ms ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 0;
      box-shadow: $shadow-focus;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &--primary {
      color: $color-accent;
      background: rgba(0, 0, 0, 0.56);
      border-color: rgba(255, 212, 59, 0.68);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);

      &:hover:not(:disabled),
      &:focus-visible:not(:disabled) {
        color: #111111;
        background: $color-accent;
        border-color: $color-accent;
      }
    }

    &--secondary {
      min-height: 34px;
      padding: 6px 14px;
      color: $color-text;
      background: rgba(0, 0, 0, 0.38);
      border: 1px solid rgba(255, 255, 255, 0.16);

      &:hover:not(:disabled) {
        color: #111111;
        background: $color-info;
        border-color: $color-info;
      }
    }
  }

  &__file-select,
  &__upload-control {
    position: relative;
  }

  &__clear-button {
    position: absolute;
    top: 50%;
    right: 10px;
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $color-danger;
    background: rgba(255, 107, 107, 0.1);
    border: 0;
    border-radius: 50%;
    transform: translateY(-50%);
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: #ffffff;
      background: $color-danger;
      outline: 0;
    }
  }

  &__separator {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px 0;
    color: #858585;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;

    &::before,
    &::after {
      height: 1px;
      flex: 1;
      background: rgba(255, 255, 255, 0.08);
      content: "";
    }
  }

  &__upload {
    label {
      display: flex;
      min-height: 108px;
      align-items: center;
      justify-content: center;
      margin: 0;
      padding: 22px 52px 22px 22px;
      overflow: hidden;
      color: #e2e2e2;
      font-size: 14px;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: rgba(0, 0, 0, 0.34);
      border: 1px dashed rgba(255, 212, 59, 0.46);
      border-radius: 12px;
      cursor: pointer;
      transition: color 160ms ease, background-color 160ms ease, border-color 160ms ease;

      &:hover {
        color: $color-info;
        background: rgba(0, 0, 0, 0.58);
        border-color: $color-info;
      }
    }

    input[type="file"] {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
    }

    input[type="file"]:focus-visible + span {
      outline: 2px solid $color-accent;
      outline-offset: 5px;
    }
  }

  &__hint {
    display: block;
    margin-top: 8px;
    color: $color-muted;
    font-size: 12px;
  }

  &__progress {
    margin-top: 18px;
    padding: 14px;
    color: $color-info;
    text-align: center;
    background: rgba(0, 0, 0, 0.42);
    border: 1px solid rgba(255, 212, 59, 0.2);
    border-radius: 10px;
  }

  &__status,
  &__description {
    color: $color-muted;
    font-size: 14px;
    line-height: 1.55;
  }

  &__section-title {
    margin: 0 0 24px;
    color: $color-text;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.35;
    text-align: center;
  }

  &__success-icon {
    display: flex;
    width: 54px;
    height: 54px;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
    color: #092416;
    font-size: 20px;
    background: linear-gradient(135deg, #7be7a6, $color-success);
    border-radius: 50%;
    box-shadow: 0 0 0 7px rgba(85, 217, 138, 0.09);
  }

  &__section-subtitle,
  &__metrics-title,
  &__sidebar-title {
    color: $color-text;
    font-size: 15px;
    font-weight: 700;
  }

  &__metrics-title {
    margin: 28px 0 14px;
  }

  &__description {
    margin: 5px 0 14px;
  }

  &__comparison {
    margin-top: 26px;
  }

  &__algorithm-card {
    margin-bottom: 9px;
  }

  &__algorithm-card-content {
    position: relative;
    padding: 14px 16px;
    background: rgba(0, 0, 0, 0.42);
    border: 1px solid $color-border;
    border-radius: 10px;

    > span {
      display: block;
    }
  }

  &__algorithm-card--recommended &__algorithm-card-content {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.62), rgba(85, 217, 138, 0.07));
    border-color: rgba(85, 217, 138, 0.32);
  }

  &__algorithm-name {
    max-width: calc(100% - 110px);
    margin-bottom: 6px;
    color: $color-text;
    font-size: 15px;
    font-weight: 700;
  }

  &__algorithm-card--recommended &__algorithm-name {
    color: $color-success;
  }

  &__algorithm-metric {
    color: $color-muted;
    font-size: 13px;
  }

  &__badge {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 5px 8px;
    color: #092416;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    background: $color-success;
    border-radius: 999px;
  }

  &__metric-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 0 0 18px;
    padding: 0;
    list-style: none;
  }

  &__metric-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__metric-option {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;

    label {
      display: inline-flex;
      min-width: 0;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-size: 13px;
      cursor: pointer;
    }
  }

  &__tooltip-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    margin-left: 6px;
    color: $color-info;
    font-size: 15px;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    transition: transform 150ms ease, color 150ms ease;

    &:hover,
    &.is-active {
      color: #ffffff;
      transform: scale(1.15);
    }
  }

  &__tooltip-dropdown {
    position: relative;
    margin-top: 8px;
    padding: 10px 14px;
    color: #f1f3f5;
    font-size: 12px;
    line-height: 1.5;
    background: rgba(18, 20, 24, 0.95);
    border: 1px solid rgba(255, 212, 59, 0.35);
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(8px);
  }

  &__tooltip-arrow {
    position: absolute;
    top: -6px;
    right: 18px;
    width: 10px;
    height: 10px;
    background: rgba(18, 20, 24, 0.95);
    border-top: 1px solid rgba(255, 212, 59, 0.35);
    border-left: 1px solid rgba(255, 212, 59, 0.35);
    transform: rotate(45deg);
  }

  .slide-fade-enter-active {
    transition: all 200ms ease-out;
  }

  .slide-fade-leave-active {
    transition: all 150ms ease-in;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }

  &__metric-values {
    margin-bottom: 20px;

    p {
      margin-bottom: 7px;
      color: #dedede;
      font-size: 14px;
    }
  }

  &__accent {
    color: $color-accent !important;
  }

  &__sidebar-title {
    margin: 0;
    padding-bottom: 20px;
    color: #fffcc4;
    font-size: 22px;
    text-transform: uppercase;
  }

  &__recent-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__recent-item {
    margin-bottom: 9px;
    padding: 12px;
    background: rgba(228, 233, 237, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 10px;
    transition: background-color 160ms ease, border-color 160ms ease;

    &:hover {
      background: rgba(0, 0, 0, 0.58);
      border-color: rgba(255, 212, 59, 0.24);
    }

    span {
      display: block;
      overflow: hidden;
      color: $color-muted;
      font-size: 12px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__recent-name {
    margin-bottom: 3px;
    color: #87cefa !important;
    font-size: 14px !important;
    font-weight: 700;
  }

  &__empty-state {
    margin: 0;
    padding: 30px 14px;
    color: $color-muted;
    font-size: 13px;
    text-align: center;
    background: rgba(0, 0, 0, 0.3);
    border: 1px dashed rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
}

@media only screen and (max-width: 991px) {
  .model-builder {
    &__content {
      padding: 22px 18px;
    }

    &__card {
      padding: 24px;
    }

    &__panel--sidebar {
      padding: 16px;
    }
  }
}

@media only screen and (max-width: 575px) {
  .model-builder {
    &__main-column {
      padding-right: 8px !important;
    }

    &__panel {
      border-radius: 12px;

      &--sidebar {
        min-height: auto;
        margin-top: 14px;
      }
    }

    &__header {
      padding: 15px;
    }

    &__title {
      font-size: 21px;
    }

    &__content {
      padding: 18px 12px;
    }

    &__card {
      padding: 18px;
    }

    &__steps {
      margin-bottom: 18px;
      padding: 0;
    }

    &__step-label {
      font-size: 10px;
    }

    &__metric-options {
      grid-template-columns: 1fr;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .model-builder * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>

<style lang="scss">
.model-builder__progress .uploading-progress-bar {
  max-width: 100% !important;
  padding-bottom: 0 !important;
}
</style>